import React, { useState, useEffect, useCallback, useRef } from 'react';
import './WhatsAppSignup.css';

const WhatsAppSignup = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Login, 2: Business Info, 3: WhatsApp Setup
  const exchangeableCodeRef = useRef(null);

  // Handle Embedded Signup messages
  const handleEmbeddedSignupMessage = useCallback(async (data) => {
    if (data.event === 'FINISH' || data.event === 'FINISH_ONLY_WABA' || data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
      // Successful completion
      // Submit data to onboarding service
      // Wait for exchangeable code if not available yet (with retry mechanism)
      const submitSignupData = async (retryCount = 0) => {
        const code = exchangeableCodeRef.current;
        
        if (!code && retryCount < 10) {
          // Wait 500ms and retry if code is not available yet
          await new Promise(resolve => setTimeout(resolve, 500));
          return submitSignupData(retryCount + 1);
        }
        
        if (!code) {
          console.error('Exchangeable token code not available after waiting');
          setStep(3);
          return;
        }

        try {
          const signupData = {
            phone_number_id: data.data.phone_number_id,
            waba_id: data.data.waba_id,
            business_id: data.data.business_id,
            exchangeable_token_code: code
          };

          const response = await fetch('https://dev-sms.physicsinseconds.com/onboarding/api/signup', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(signupData)
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error('API Error Response:', errorText);
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          await response.json();
          
          // Update UI to show success
          setStep(3);
        } catch (error) {
          console.error('Error submitting signup data:', error);
          // Still show success step, but user will see error in console
          setStep(3);
        }
      };

      await submitSignupData();
    } else if (data.event === 'CANCEL') {
      // User abandoned the flow
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Initialize Facebook SDK for Embedded Signup
    const initFB = () => {
      if (window.FB) {
        window.FB.init({
          appId: '1273344307866049',
          autoLogAppEvents: true,
          xfbml: true,
          version: 'v23.0'
        });
      } else {
        // Set up the async init function
        window.fbAsyncInit = function() {
          window.FB.init({
            appId: '1273344307866049',
            autoLogAppEvents: true,
            xfbml: true,
            version: 'v23.0'
          });
        };
      }
    };

    // Try to initialize immediately
    initFB();

    // Also set up the async init in case FB loads later
    window.fbAsyncInit = function() {
      window.FB.init({
        appId: '1273344307866049',
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v23.0'
      });
    };

    // Session logging message event listener for Embedded Signup
    const handleMessage = (event) => {
      if (!event.origin.endsWith('facebook.com')) return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          handleEmbeddedSignupMessage(data);
        }
      } catch {
        // Silently handle parse errors
      }
    };

    window.addEventListener('message', handleMessage);

    // Cleanup
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleEmbeddedSignupMessage]);

  // Response callback for Embedded Signup
  const fbLoginCallback = (response) => {
    if (response.authResponse) {
      const code = response.authResponse.code;
      exchangeableCodeRef.current = code; // Store in ref for immediate access
      // Code will be submitted automatically when signup completes
      // The code expires in 30 seconds!
    } else {
      setIsLoading(false);
    }
  };

  // Launch WhatsApp Embedded Signup
  const launchWhatsAppSignup = () => {
    if (!window.FB) {
      console.error('Facebook SDK not loaded');
      alert('Facebook SDK not loaded. Please refresh the page and try again.');
      return;
    }

    setIsLoading(true);
    
    // Ensure FB is initialized before calling login
    if (!window.FB.getLoginStatus) {
      window.FB.init({
        appId: '1273344307866049',
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v23.0'
      });
    }

    window.FB.login(fbLoginCallback, {
      config_id: '1770460276928945', // Your Configuration ID
      response_type: 'code',
      override_default_response_type: true,
      extras: {
        setup: {},
        featureType: '', // Leave blank for default flow
        sessionInfoVersion: '3'
      }
    });
  };

  const handleLogout = () => {
    window.FB.logout(() => {
      setUserProfile(null);
      setStep(1);
    });
  };

  const handleBusinessInfoSubmit = (e) => {
    e.preventDefault();
    setStep(3);
  };


  return (
    <div className="whatsapp-signup">
      <div className="container">
        <div className="signup-header">
          <h1>WhatsApp Business Platform Setup</h1>
          <p>Get started with our WhatsApp Business messaging solution. Connect your Facebook business account to begin the setup process.</p>
        </div>

        <div className="signup-steps">
          <div className="step-indicator">
            <div className={`step ${step >= 1 ? 'active' : ''}`}>
              <span className="step-number">1</span>
              <span className="step-label">Facebook Login</span>
            </div>
            <div className={`step ${step >= 2 ? 'active' : ''}`}>
              <span className="step-number">2</span>
              <span className="step-label">Business Info</span>
            </div>
            <div className={`step ${step >= 3 ? 'active' : ''}`}>
              <span className="step-number">3</span>
              <span className="step-label">WhatsApp Setup</span>
            </div>
          </div>

          <div className="signup-content">
            {step === 1 && (
              <div className="login-step">
                <div className="login-card">
                  <h2>Connect with Facebook</h2>
                  <p>To set up WhatsApp Business Platform, you need to connect your Facebook business account.</p>
                  
                  <div className="facebook-login">
                    <button 
                      className="btn-facebook"
                      onClick={launchWhatsAppSignup}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <span className="loading-spinner"></span>
                      ) : (
                        <>
                          <svg className="facebook-icon" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                          Setup WhatsApp Business Account
                        </>
                      )}
                    </button>
                  </div>

                  <div className="login-benefits">
                    <h3>What you'll get access to:</h3>
                    <ul>
                      <li>WhatsApp Business API integration</li>
                      <li>Customer conversation management</li>
                      <li>Broadcast messaging campaigns</li>
                      <li>Automated bot responses</li>
                      <li>Analytics and reporting</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && userProfile && (
              <div className="business-info-step">
                <div className="user-profile">
                  <img src={userProfile.picture?.data?.url} alt="Profile" className="profile-pic" />
                  <div className="profile-info">
                    <h3>Welcome, {userProfile.name}!</h3>
                    <p>{userProfile.email}</p>
                  </div>
                  <button className="btn-logout" onClick={handleLogout}>Logout</button>
                </div>

                <form className="business-form" onSubmit={handleBusinessInfoSubmit}>
                  <h2>Business Information</h2>
                  <div className="form-group">
                    <label htmlFor="businessName">Business Name *</label>
                    <input
                      type="text"
                      id="businessName"
                      name="businessName"
                      required
                      placeholder="Enter your business name"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="businessType">Business Type *</label>
                    <select id="businessType" name="businessType" required>
                      <option value="">Select business type</option>
                      <option value="retail">Retail</option>
                      <option value="restaurant">Restaurant</option>
                      <option value="healthcare">Healthcare</option>
                      <option value="finance">Finance</option>
                      <option value="education">Education</option>
                      <option value="technology">Technology</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="phoneNumber">Business Phone Number *</label>
                    <input
                      type="tel"
                      id="phoneNumber"
                      name="phoneNumber"
                      required
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="website">Website (Optional)</label>
                    <input
                      type="url"
                      id="website"
                      name="website"
                      placeholder="https://yourbusiness.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="expectedVolume">Expected Monthly Messages</label>
                    <select id="expectedVolume" name="expectedVolume">
                      <option value="0-1000">0 - 1,000 messages</option>
                      <option value="1000-5000">1,000 - 5,000 messages</option>
                      <option value="5000-10000">5,000 - 10,000 messages</option>
                      <option value="10000+">10,000+ messages</option>
                    </select>
                  </div>

                  <button type="submit" className="btn btn-primary">
                    Continue to WhatsApp Setup
                  </button>
                </form>
              </div>
            )}

            {step === 3 && (
              <div className="whatsapp-setup-step">
                <div className="setup-card">
                  <div className="success-icon">
                    <span>✅</span>
                  </div>
                  <h2>Sign Up Completed Successfully!</h2>
                  <p>Your WhatsApp Business Account signup has been completed successfully. You will be receiving information to Kazenly platform within 24 hours on registered email address.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSignup;
